import { useState } from "react";
import { SENSITIVE_DATA_UI_ENABLED } from "@/lib/features";
import { Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { PRODUCT_NAME } from "@shared/brand";
import { Seo } from "@/components/public/Seo";

const FAQ_ITEMS = [
  ...(SENSITIVE_DATA_UI_ENABLED ? [{
    q: "Do you store my SIN, account numbers, or passwords?",
    a: `No. ${PRODUCT_NAME} never stores sensitive financial information like Social Insurance Numbers, bank account numbers, credit card numbers, or passwords. This information stays in a secure file on your computer — not on our servers. When you download pre-filled letters or government forms, any sensitive fields are highlighted in yellow for you to complete manually from your own records. This is a deliberate security design: even if our systems were compromised, your most sensitive data isn't here.`,
  }] : []),
  {
    q: `What is ${PRODUCT_NAME}?`,
    a: `${PRODUCT_NAME} is a secure platform that helps you organize everything your executor will need — accounts, documents, wishes, and contacts — in one place. It's designed for individuals working with a financial advisor or on their own.`,
  },
  {
    q: "What's the difference between Essential and Complete?",
    a: "Essential covers core estate documents and executor information. Complete adds financial accounts, digital assets, government forms, and the full suite of executor tools. Your advisor can help you choose the right plan.",
  },
  {
    q: "How does the executor access the estate plan?",
    a: "Your advisor or you invite them by email. They receive a secure login and can only see the information you've granted them access to.",
  },
  {
    q: "Can I change my executor?",
    a: "Yes, at any time. Go to the Executors section of your dashboard to add, remove, or update executor access.",
  },
  {
    q: "What happens after the free trial?",
    a: "You'll be prompted to choose a plan. Your data is never deleted immediately — we'll always give you time to export or upgrade before any data removal.",
  },
  {
    q: "How do I cancel my account?",
    a: "Contact us using the form below and we'll process your request within 1 business day. We'll confirm the cancellation and let you know about any data export options.",
  },
  {
    q: "What if I need help from a lawyer?",
    a: "We recommend consulting an estate lawyer for complex situations. Use the Professional Contacts section of your dashboard to store your lawyer's details so your executor can reach them easily.",
  },
  {
    q: "How do I export my data?",
    a: "From your dashboard, use the Ontario Forms Export or Estate Summary to download your estate information. Advisors can also generate PDF reports.",
  },
];

const SUBJECT_OPTIONS = [
  "General Question",
  "Technical Issue",
  "Billing",
  "Feature Request",
  "Other",
] as const;

const contactSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Enter a valid email address"),
  subject: z.enum(SUBJECT_OPTIONS, { required_error: "Select a subject" }),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

type ContactFormData = z.infer<typeof contactSchema>;

export default function SupportPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: { name: "", email: "", subject: undefined, message: "" },
  });

  const onSubmit = async (data: ContactFormData) => {
    setSubmitError(null);
    try {
      const res = await fetch("/api/support/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (res.status === 429) {
        setSubmitError("You've submitted too many requests. Please wait an hour and try again.");
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setSubmitError((body as any).error ?? "Failed to send message. Please try again.");
        return;
      }
      setSubmitted(true);
    } catch {
      setSubmitError("Failed to send message. Please check your connection and try again.");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo path="/support" />
      {/* Header */}
      <header className="nav-header px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <p className="text-muted-foreground text-sm font-medium tracking-wide mb-1">{PRODUCT_NAME}</p>
          <h1 className="text-2xl font-bold text-white">How can we help?</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Browse our FAQ or send us a message — we'll respond within 1 business day.
          </p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-10">
        {/* FAQ */}
        <section>
          <h2 className="text-lg font-semibold text-foreground mb-4">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border border-border rounded-lg px-4"
              >
                <AccordionTrigger className="text-left font-medium hover:no-underline py-4">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-4">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Contact form */}
        <section>
          <h2 className="text-lg font-semibold text-foreground mb-4">Contact Us</h2>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Send a message</CardTitle>
              <CardDescription>
                We'll respond to your email within 1 business day.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {submitted ? (
                <div className="flex flex-col items-center py-8 text-center gap-3">
                  <CheckCircle className="w-12 h-12 text-primary" />
                  <h3 className="text-lg font-semibold">Message sent!</h3>
                  <p className="text-muted-foreground text-sm max-w-sm">
                    We received your message and will respond within 1 business day. Check your inbox for a confirmation.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-2"
                    onClick={() => { setSubmitted(false); form.reset(); }}
                  >
                    Send another message
                  </Button>
                </div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Your name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="you@example.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Subject</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a topic" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {SUBJECT_OPTIONS.map((opt) => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Message</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Describe your question or issue..."
                              rows={5}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {submitError && (
                      <p className="text-sm text-destructive">{submitError}</p>
                    )}
                    <Button
                      type="submit"
                      disabled={form.formState.isSubmitting}
                      className="w-full sm:w-auto"
                    >
                      {form.formState.isSubmitting ? "Sending…" : "Send Message"}
                    </Button>
                  </form>
                </Form>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Footer */}
        <div className="text-center pb-6">
          <Link href="/login">
            <span className="text-sm text-primary hover:underline cursor-pointer">
              <ArrowLeft className="h-3.5 w-3.5 inline-block align-[-2px]" aria-hidden="true" /> Back to login
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
}
